import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  LibPipeModule,
  LibDirectiveModule,
  FeatureToggleModule,
  SecurityModule,
  TimeSpentPipe,
  ShowMoreComponent,
  DualsIndicatorComponent,
} from '@mynexus/mynexus-ui-lib';
import { DocumentsUploadModule, DocumentsUploadPACIMModule, FormErrorsModule, ShowMoreModule } from '@shared/modules';

// Components
import { AuthRequestAuthorizationComponent } from './component/auth-request/auth-request-authorization/auth-request-authorization.component';
import { AuthRequestCompletionComponent } from './component/auth-request/auth-request-completion/auth-request-completion.component';
import { AuthRequestDiagnosisComponent } from './component/auth-request/auth-request-diagnosis/auth-request-diagnosis.component';
import { AuthRequestDocumentsComponent } from './component/auth-request/auth-request-documents/auth-request-documents.component';
import { AuthRequestHomeboundComponent } from './component/auth-request/auth-request-homebound/auth-request-homebound.component';
import { AuthRequestPhysicianComponent } from './component/auth-request/auth-request-physician/auth-request-physician.component';
import { AuthRequestProviderComponent } from './component/auth-request/auth-request-provider/auth-request-provider.component';
import { AuthRequestServicesComponent } from './component/auth-request/auth-request-services/auth-request-services.component';
import { AuthReviewComponent } from './component/auth-request/auth-review/auth-review.component';
import { CertGridComponent } from './component/cert-grid/cert-grid.component';
import { ClaimSearchComponent } from './component/claim-search/claim-search.component';
import { DiagnosisNewComponent } from './component/diagnosis-new/diagnosis-new.component';
import { DiagnosisOriginalCodesComponent } from './component/diagnosis-new/diagnosis-original-codes/diagnosis-original-codes.component';
import { FooterComponent } from './component/footer/footer.component';
import { MemberDetailHeaderComponent } from './component/member-detail-header/member-detail-header.component';
import { NoDataComponent } from './component/no-data/no-data.component';

import { OralNotificationsComponent } from './component/oral-notifications/oral-notifications.component';
import { SidebarComponent } from './component/sidebar/sidebar.component';
import { SpecialInstructionsComponent } from './component/special-instructions/special-instructions.component';
import { NegotiationPvpsComponent } from './component/negotiation/negotiation-pvps/negotiation-pvps.component';
import { NegotiationCummulativeComponent } from './component/negotiation/negotiation-cummulative/negotiation-cummulative.component';

import { HomePageBlockComponent } from './component/home-page-block/home-page-block.component';
import { DenialRationaleComponent } from './component/denial-rationale/denial-rationale.component';
import { IdleComponent } from './component/idle/idle.component';
import { HippsCodeDialogComponent } from './component/hipps-code-dialog/hipps-code-dialog.component';
import { PhysicianVerificationComponent } from './component/provider/physician-verification/physician-verification.component';

// Modules (App)
import { PipeModule } from '@shared/pipe/pipe.module';
import { DirectiveModule } from '@shared/directive/directive.module';

// ThirdParty
import { PrimeNgModule } from '@shared/prime-ng/prime-ng.module';
import { CertPeriodsDialogComponent } from './component/cert-periods-dialog/cert-periods-dialog.component';
import { OralNotificationsFormComponent } from './component/oral-notifications-form/oral-notifications-form.component';
import { OralNotificationsDialogComponent } from './component/oral-notifications-dialog/oral-notifications-dialog.component';
import { CertPeriodSelectComponent } from './component/cert-period-select/cert-period-select.component';

import { AuthorInfoComponent } from './component/author-info/author-info.component';
import { ServiceProviderComponent } from './component/provider/service-provider/service-provider.component';
import { PhysicianProviderComponent } from './component/provider/physician-provider/physician-provider.component';
import { ServiceProviderSearchComponent } from './component/provider/service-provider-search/service-provider-search.component';
// tslint:disable-next-line: max-line-length
import { ServiceProviderSelectAddressComponent } from './component/provider/service-provider-select-address/service-provider-select-address.component';
import { PhysicianSelectComponent } from './component/provider/physician-select/physician-select.component';
import { PhysicianSearchComponent } from './component/provider/physician-search/physician-search.component';
import { ProviderHistoryTableComponent } from './component/provider/provider-history-table/provider-history-table.component';
import { AddPhysicianComponent } from './component/provider/add-physician/add-physician.component';
import { NotFoundFeatureComponent } from './component/not-found-feature/not-found-feature.component';
import { MemberDetailRibbonComponent } from './component/member-detail-ribbon/member-detail-ribbon.component';
import { GeneralSearchPanelComponent } from './component/general-search-panel/general-search-panel.component';
import { DiagnosisSearchComponent } from './component/pacim-diagnosis/diagnosis-search/diagnosis-search.component';
import { DiagnosisViewListsComponent } from './component/pacim-diagnosis/diagnosis-view-lists/diagnosis-view-lists.component';
import { AuthProviderSelectComponent } from './component/auth-provider-select/auth-provider-select.component';
import { NewProviderComponent } from './component/new-provider/new-provider.component';
import { SharedServiceComponent } from './component/shared-service/shared-service.component';
import { AuthRequestImmediateContactComponent } from './component/auth-request/auth-request-immediate-contact/auth-request-immediate-contact.component';
import { ClaimSearchDetailsComponent } from './component/claim-search-details/claim-search-details.component';
import { MicroAppComponent } from './component/micro-app/micro-app.component';
import { ServicingFacilitySelectComponent } from './component/servicing-facility-select/servicing-facility-select.component';
import { ServicingFacilityContactDetailsComponent } from './component/servicing-facility-contact-details/servicing-facility-contact-details.component';
import { PacDoNextDialogComponent } from './component/pac-do-next-dialog/pac-do-next-dialog.component';
import { ClinicalInformationTabComponent } from './component/clinical-information-tab/clinical-information-tab.component';
import { DocumentsTabPacComponent } from './component/documents-tab-pac/documents-tab-pac.component';
import { DiagnosisTabPacComponent } from './component/diagnosis-tab-pac/diagnosis-tab-pac.component';
import { AuthDetailsTabPacComponent } from './component/auth-details-tab-pac/auth-details-tab-pac.component';
import { GoldCardTabComponent } from './component/gold-card-tab/gold-card-tab.component';
import { CompleteTabComponent } from './component/complete-tab/complete-tab.component';
import { AuthorizationDetailsComponent } from './component/preview-tab-pac/authorization-details/authorization-details.component';
import { ClinicalInformationDetailComponent } from './component/preview-tab-pac/clinical-information-detail/clinical-information-detail.component';
import { NursingCareComponent } from './component/preview-tab-pac/clinical-information-detail/nursing-care/nursing-care.component';
import { OrthopedicConditionComponent } from './component/preview-tab-pac/clinical-information-detail/orthopedic-condition/orthopedic-condition.component';
import { StrokeComponent } from './component/preview-tab-pac/clinical-information-detail/stroke/stroke.component';
import { DiagnosisDetailsComponent } from './component/preview-tab-pac/diagnosis-details/diagnosis-details.component';
import { DocumentDetailsComponent } from './component/preview-tab-pac/document-details/document-details.component';
import { OrderingProviderComponent } from './component/preview-tab-pac/ordering-provider/ordering-provider.component';
import { ServicingFacilityComponent } from './component/preview-tab-pac/servicing-facility/servicing-facility.component';
import { ServicingFacilityNewPanelComponent } from './component/preview-tab-pac/servicing-facility/servicing-facility-new-panel/servicing-facility-new-panel.component';
import { ServicingFacilityPanelImmediateComponent } from './component/preview-tab-pac/servicing-facility/servicing-facility-panel-immediate/servicing-facility-panel-immediate.component';
import { ServicingFacilityPanelInfoComponent } from './component/preview-tab-pac/servicing-facility/servicing-facility-panel-info/servicing-facility-panel-info.component';
import { PreviewTabPacComponent } from './component/preview-tab-pac/preview-tab-pac.component';

import { ServicingFacilityContactInfoComponent } from './component/servicing-facility-tab/servicing-facility-info/servicing-facility-contact-info/servicing-facility-contact-info.component';
import { ServicingFacilityGeneralInfoComponent } from './component/servicing-facility-tab/servicing-facility-info/servicing-facility-general-info/servicing-facility-general-info.component';
import { ServicingFacilityInfoComponent } from './component/servicing-facility-tab/servicing-facility-info/servicing-facility-info.component';
import { ServicingFacilityNewComponent } from './component/servicing-facility-tab/servicing-facility-new/servicing-facility-new.component';
import { ServicingFacilityTabComponent } from './component/servicing-facility-tab/servicing-facility-tab.component';
import { OrderingProviderTabPacComponent } from './component/ordering-provider-tab-pac/ordering-provider-tab-pac.component';
import { OrderingProviderSelectComponent } from './component/ordering-provider-tab-pac/ordering-provider-select/ordering-provider-select.component';
import { OrderingProviderAccountComponent } from './component/ordering-provider-tab-pac/ordering-provider-account/ordering-provider-account.component';
import { ProviderSearchComponent } from './component/provider-search/provider-search.component';
import { UserInstructionComponent } from './component/user-instruction/user-instruction.component';
import { AuthorizationOfferComponent } from './component/authorization-offer/authorization-offer.component';
import { BenefitPlanSelectorComponent } from './component/benefit-plan-selector/benefit-plan-selector.component';

import { UserSelectorComponent } from './component/user-selector/user-selector.component';
import { ServiceProviderHHComponent } from './component/provider/service-provider-home-health/service-provider-home-health.component';
import { EnrollmentDetailsPacimComponent } from './component/enrollment-details-pac/enrollment-details-pac.component';
import { FormGeneratorComponent } from './component/form-generator/form-generator.component';
import { MfeCoreModule } from '@mynexus/mfe-core';
import { KeyFilterModule } from 'primeng/keyfilter';
import { UserWithIdsSelectorComponent } from './component/user-selector/user-with-ids-selector/user-with-ids-selector/user-with-ids-selector.component';
import { PrimaryCarePhysicianHistoryComponent } from './component/primary-care-physician-history/primary-care-physician-history.component';
import { ConfirmationDialogComponent } from './component/confirmation-dialog/confirmation-dialog.component';
import { ProductSelectorComponent } from './component/product-selector/product-selector.component';
import { SuggestedChangesDialogComponent } from './component/suggested-changes-dialog/suggested-changes-dialog.component';
import { RouteQueueDialogComponent } from './component/route-queue-modal/route-queue-modal.component';
import { InterruptedStayDialogComponent } from './component/interrupted-stay-dialog/interrupted-stay-dialog.component';
import { InterruptedStayTabPacComponent } from './component/interrupted-stay-tab-pac/interrupted-stay-tab-pac.component';
import { ServiceLineCountdownComponent } from './component/turne-around-time-counter/service-line-countdown.component';
import { QueueCountDownComponent } from './component/countdown/queue-countdown.component';
import { TherapyMinutesTableComponent } from './component/therapy-minutes-table/therapy-minutes-table.component';
import { LevelChangeDialogComponent } from './component/level-change-dialog/level-change-dialog.component';
import { TherapyMinutesExpandedComponent } from './component/therapy-minutes-table/therapy-minutes-expanded/therapy-minutes-expanded.component';
import { NextReviewPeriodRequestModule } from './component/next-review-period-request/next-review-period-request.module';
import { RequestVisitsDialogComponent } from './component/request-visits-dialog/request-visits-dialog.component';
import { AspireWidgetComponent } from '@feature/member-detail/aspire-widget/aspire-widget.component';
import { DoNextQueueDialogComponent } from './component/do-next-queue-dialog/do-next-queue-dialog.component';
import { DuplicateRequestVisitsDialogComponent } from './component/duplicate-request-visits-dialog/duplicate-request-visits-dialog.component';
import { QueueCountUpComponent } from './component/countup/queue-countup.component';
import { ServiceLineCountupComponent } from './component/turn-around-countup-timer/service-line-countup/service-line-countup.component';
import { InstructionComponent } from './component/instruction/instruction.component';
import { CustomMessageComponent } from './component/custom-message/custom-message.component';
import { StateSelectorComponent } from './component/state-selector/state-selector.component';

@NgModule({
  imports: [
    CommonModule,
    DirectiveModule,
    DocumentsUploadModule,
    DocumentsUploadPACIMModule,
    FeatureToggleModule,
    FormErrorsModule,
    InstructionComponent,
    FormsModule,
    KeyFilterModule,
    LibDirectiveModule,
    LibPipeModule,
    MfeCoreModule,
    NextReviewPeriodRequestModule,
    PipeModule,
    PrimeNgModule,
    ReactiveFormsModule,
    SecurityModule,
    ShowMoreModule,
    ShowMoreComponent,
    DualsIndicatorComponent,
  ],
  declarations: [
    AddPhysicianComponent,
    AspireWidgetComponent,
    AuthDetailsTabPacComponent,
    AuthDetailsTabPacComponent,
    AuthProviderSelectComponent,
    AuthRequestAuthorizationComponent,
    AuthRequestCompletionComponent,
    AuthRequestDiagnosisComponent,
    AuthRequestDocumentsComponent,
    AuthRequestHomeboundComponent,
    AuthRequestImmediateContactComponent,
    AuthRequestPhysicianComponent,
    AuthRequestProviderComponent,
    AuthRequestServicesComponent,
    AuthReviewComponent,
    AuthorInfoComponent,
    AuthorizationDetailsComponent,
    AuthorizationOfferComponent,
    BenefitPlanSelectorComponent,
    CertGridComponent,
    CertPeriodSelectComponent,
    CertPeriodsDialogComponent,
    ClaimSearchComponent,
    ClaimSearchDetailsComponent,
    ClinicalInformationDetailComponent,
    ClinicalInformationTabComponent,
    CompleteTabComponent,
    ConfirmationDialogComponent,
    CustomMessageComponent,
    DenialRationaleComponent,
    DiagnosisDetailsComponent,
    DiagnosisNewComponent,
    DiagnosisOriginalCodesComponent,
    DiagnosisSearchComponent,
    DiagnosisTabPacComponent,
    DiagnosisViewListsComponent,
    DoNextQueueDialogComponent,
    DocumentDetailsComponent,
    DocumentsTabPacComponent,
    DuplicateRequestVisitsDialogComponent,
    EnrollmentDetailsPacimComponent,
    FooterComponent,
    FormGeneratorComponent,
    GeneralSearchPanelComponent,
    GoldCardTabComponent,
    HippsCodeDialogComponent,
    HomePageBlockComponent,
    IdleComponent,
    InterruptedStayDialogComponent,
    InterruptedStayTabPacComponent,
    LevelChangeDialogComponent,
    MemberDetailHeaderComponent,
    MemberDetailRibbonComponent,
    MicroAppComponent,
    NegotiationCummulativeComponent,
    NegotiationPvpsComponent,
    NewProviderComponent,
    NoDataComponent,
    NotFoundFeatureComponent,
    NursingCareComponent,
    OralNotificationsComponent,
    OralNotificationsDialogComponent,
    OralNotificationsFormComponent,
    OrderingProviderAccountComponent,
    OrderingProviderComponent,
    OrderingProviderSelectComponent,
    OrderingProviderTabPacComponent,
    OrthopedicConditionComponent,
    PacDoNextDialogComponent,
    PhysicianProviderComponent,
    PhysicianSearchComponent,
    PhysicianSelectComponent,
    PhysicianVerificationComponent,
    PreviewTabPacComponent,
    PrimaryCarePhysicianHistoryComponent,
    ProductSelectorComponent,
    ProviderHistoryTableComponent,
    ProviderSearchComponent,
    ProviderSearchComponent,
    QueueCountDownComponent,
    QueueCountUpComponent,
    RequestVisitsDialogComponent,
    RouteQueueDialogComponent,
    ServiceLineCountdownComponent,
    ServiceLineCountupComponent,
    ServiceLineCountupComponent,
    ServiceProviderComponent,
    ServiceProviderHHComponent,
    ServiceProviderSearchComponent,
    ServiceProviderSelectAddressComponent,
    ServicingFacilityComponent,
    ServicingFacilityContactDetailsComponent,
    ServicingFacilityContactInfoComponent,
    ServicingFacilityGeneralInfoComponent,
    ServicingFacilityInfoComponent,
    ServicingFacilityNewComponent,
    ServicingFacilityNewPanelComponent,
    ServicingFacilityPanelImmediateComponent,
    ServicingFacilityPanelInfoComponent,
    ServicingFacilitySelectComponent,
    ServicingFacilityTabComponent,
    SharedServiceComponent,
    SidebarComponent,
    SpecialInstructionsComponent,
    StateSelectorComponent,
    StrokeComponent,
    SuggestedChangesDialogComponent,
    TherapyMinutesExpandedComponent,
    TherapyMinutesTableComponent,
    UserInstructionComponent,
    UserSelectorComponent,
    UserWithIdsSelectorComponent,
  ],
  exports: [
    AddPhysicianComponent,
    AspireWidgetComponent,
    AuthDetailsTabPacComponent,
    AuthProviderSelectComponent,
    AuthRequestAuthorizationComponent,
    AuthRequestCompletionComponent,
    AuthRequestDiagnosisComponent,
    AuthRequestDocumentsComponent,
    AuthRequestHomeboundComponent,
    AuthRequestImmediateContactComponent,
    AuthRequestPhysicianComponent,
    AuthRequestProviderComponent,
    AuthRequestServicesComponent,
    AuthReviewComponent,
    AuthorInfoComponent,
    AuthorizationDetailsComponent,
    AuthorizationOfferComponent,
    BenefitPlanSelectorComponent,
    CertGridComponent,
    CertPeriodSelectComponent,
    ClaimSearchComponent,
    ClinicalInformationDetailComponent,
    ClinicalInformationTabComponent,
    CommonModule,
    CompleteTabComponent,
    CustomMessageComponent,
    DenialRationaleComponent,
    DiagnosisDetailsComponent,
    DiagnosisNewComponent,
    DiagnosisSearchComponent,
    DiagnosisTabPacComponent,
    DiagnosisViewListsComponent,
    DirectiveModule,
    DoNextQueueDialogComponent,
    DocumentDetailsComponent,
    DocumentsTabPacComponent,
    DocumentsUploadPACIMModule,
    EnrollmentDetailsPacimComponent,
    FeatureToggleModule,
    FooterComponent,
    FormErrorsModule,
    FormGeneratorComponent,
    FormsModule,
    GoldCardTabComponent,
    HippsCodeDialogComponent,
    HomePageBlockComponent,
    IdleComponent,
    InterruptedStayDialogComponent,
    InterruptedStayTabPacComponent,
    KeyFilterModule,
    LevelChangeDialogComponent,
    LibDirectiveModule,
    LibPipeModule,
    MemberDetailHeaderComponent,
    MemberDetailRibbonComponent,
    MfeCoreModule,
    MicroAppComponent,
    NegotiationCummulativeComponent,
    NegotiationPvpsComponent,
    NewProviderComponent,
    NextReviewPeriodRequestModule,
    NoDataComponent,
    NotFoundFeatureComponent,
    NursingCareComponent,
    OralNotificationsComponent,
    OralNotificationsFormComponent,
    OrderingProviderAccountComponent,
    OrderingProviderComponent,
    OrderingProviderSelectComponent,
    OrderingProviderTabPacComponent,
    OrthopedicConditionComponent,
    PhysicianProviderComponent,
    PhysicianSearchComponent,
    PhysicianSelectComponent,
    PhysicianVerificationComponent,
    PipeModule,
    PreviewTabPacComponent,
    PrimaryCarePhysicianHistoryComponent,
    PrimeNgModule,
    ProductSelectorComponent,
    ProviderHistoryTableComponent,
    ProviderSearchComponent,
    QueueCountDownComponent,
    QueueCountUpComponent,
    ReactiveFormsModule,
    RequestVisitsDialogComponent,
    RouteQueueDialogComponent,
    SecurityModule,
    ServiceLineCountdownComponent,
    ServiceLineCountupComponent,
    ServiceProviderComponent,
    ServiceProviderHHComponent,
    ServiceProviderSearchComponent,
    ServiceProviderSelectAddressComponent,
    ServicingFacilityComponent,
    ServicingFacilityContactDetailsComponent,
    ServicingFacilityContactInfoComponent,
    ServicingFacilityGeneralInfoComponent,
    ServicingFacilityInfoComponent,
    ServicingFacilityNewComponent,
    ServicingFacilityNewPanelComponent,
    ServicingFacilityPanelImmediateComponent,
    ServicingFacilityPanelInfoComponent,
    ServicingFacilitySelectComponent,
    ServicingFacilityTabComponent,
    ShowMoreModule,
    SidebarComponent,
    SpecialInstructionsComponent,
    StateSelectorComponent,
    StrokeComponent,
    SuggestedChangesDialogComponent,
    TherapyMinutesExpandedComponent,
    TherapyMinutesTableComponent,
    UserInstructionComponent,
    UserSelectorComponent,
    UserWithIdsSelectorComponent,
    DualsIndicatorComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    TimeSpentPipe,
  ],
})
export class SharedModule {}
